/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { fromEnumAsString, isNullOrUndefined } from 'in-time-core';
import { logError, logInfo } from 'in-time-logger';
import { Renderer2 } from '@angular/core';
import { AnalyticsTracker } from './analytics-tracker';
import {
  AnalyticsTrackerType, CartEventArgs, ContentViewEventArgs, ExploreEventArgs, PageViewEventArgs,
  PromoCodeEventArgs, SearchEventArgs, SignUpEventArgs
} from './analytics.types';
import {
  GTM_ADD_TO_CART, GTM_COMPLETE_REGISTRATION, GTM_CONTENT_VIEW,
  GTM_EXPLORE, GTM_INITIATE_CHECKOUT, GTM_INITIATE_PURCHASE,
  GTM_PROMO_CODE_ENTERED, GTM_PURCHASE, GTM_PURCHASE_CANCELED,
  GTM_PURCHASE_FAILED, GTM_SEARCH, GTM_SIGN_UP,
} from './analytics.events';
import { GtmHelper, TagManagerEntry } from '../helpers/gtm-helpers';

const TAG_MANAGER_ID: string = 'GTM-MLJZNC4V';

export class SandboxGtmTracker extends AnalyticsTracker {
  private hasAcceptedCookies: boolean = false;
  private hasBeenInitialized: boolean = false;

  private tagManager: TagManagerEntry | null = null;

  public readonly $typeId: AnalyticsTrackerType = 'sandbox-gtm';

  static create(opts: {
    renderer: Renderer2,
    document: Document,
  }): SandboxGtmTracker {
    return new SandboxGtmTracker(opts.renderer, opts.document);
  }

  private constructor(
    private readonly renderer: Renderer2,
    private readonly _document: Document,
  ) { super(); }

  onInit(hasAcceptedCookies: boolean): void {
    this.hasAcceptedCookies = hasAcceptedCookies;

    try {
      if(this.tagManager == null) {
        const { elementId, dataLayerName } = GtmHelper.createGoogleTagManager({
          document: this._document,
          renderer: this.renderer,
          tagManagerId: TAG_MANAGER_ID,
          hasConsent: this.hasAcceptedCookies,
        });

        this.tagManager = { tagManagerId: TAG_MANAGER_ID, elementId, dataLayerName };
      }

      this.hasBeenInitialized = true;
      logInfo(`Sandbox Google Tag Manager has been activated. Cookies have ${this.hasAcceptedCookies ? 'been' : 'not been'} accepted.`);
    }
    catch(error) {
      this.hasBeenInitialized = false;
      logError('Failed to activate Sandbox Google Tag Manager.', error);
    }
  }

  onDestroy(): void {
    // nothing to do here :)
  }

  onCookiesAccepted(): void {
    this.hasAcceptedCookies = true;
    this.trackConsent();
  }

  onCookiesDeclined(): void {
    this.hasAcceptedCookies = false;
    this.trackConsent();
  }

  trackPromoCode(args: PromoCodeEventArgs): void {
    // this is a custom event, which is somewhat useful, so it can stay
    this.track({
      analyticsEventName: GTM_PROMO_CODE_ENTERED,
      extraData: {
        promo_code: args.promoCode,
      },
    });
  }

  trackPageView(args: PageViewEventArgs): void {
    // skip, because there is no such thing as page_view event on google analytics
  }

  trackSignUp(args: SignUpEventArgs): void {
    // skip, because it needs a totally different logic:
    // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=11828619753363400125-EU&client_type=gtag#sign_up
  }

  trackSearch(args: SearchEventArgs): void {
    this.track({
      analyticsEventName: GTM_SEARCH,
      extraData: {
        search_term: args.query
      }
    });
  }

  trackExplore(args: ExploreEventArgs): void {
    // this is a custom event, which is somewhat useful, so it can stay
    this.track({
      analyticsEventName: GTM_EXPLORE,
      extraData: {
        route: args.id,
        via: args.via,
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      },
    });
  }

  trackContentView(args: ContentViewEventArgs): void {
    // skip, because this needs a bigger refactor to make it work
    // the actual event is called view_item (view_item_list / view_cart / view_promotion)
    // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=11828619753363400125-EU&client_type=gtag#view_item
  }

  trackAddToCart(args: CartEventArgs): void {
    this.track({
      analyticsEventName: GTM_ADD_TO_CART,
      ecommerce: {
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  trackInitiateCheckout(args: CartEventArgs): void {
    this.track({
      analyticsEventName: GTM_INITIATE_CHECKOUT,
      ecommerce: {
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  trackCompleteRegistration(args: CartEventArgs): void {
    // skip, because this is redundant and there is no good standard event to map it to
  }

  trackInitiatePurchase(args: CartEventArgs): void {
    // skip, because this is redundant and there is no good standard event to map it to
  }

  trackPurchaseComplete(args: CartEventArgs): void {
    this.track({
      analyticsEventName: GTM_PURCHASE,
      ecommerce: {
        transaction_id: args.deduplicationId,
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  trackPurchaseFailed(args: CartEventArgs): void {
    // this is a custom event, which is somewhat useful, so it can stay
    this.track({
      analyticsEventName:GTM_PURCHASE_FAILED,
      ecommerce: {
        transaction_id: args.deduplicationId,
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  trackPurchaseCanceled(args: CartEventArgs): void {
    // this is a custom event, which is somewhat useful, so it can stay
    this.track({
      analyticsEventName: GTM_PURCHASE_CANCELED,
      ecommerce: {
        transaction_id: args.deduplicationId,
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  private track({
    analyticsEventName,
    ecommerce = undefined,
    extraData = undefined,
  }: {
    analyticsEventName: string,
    ecommerce?: object,
    extraData?: object,
  }): void {
    if(!this.hasBeenInitialized || !this.hasAcceptedCookies) return;

    try {
      if(isNullOrUndefined(this.tagManager)) {
        return;
      }

      GtmHelper.logEventToGtmTracker({
        analyticsEventName,
        dataLayerName: this.tagManager.dataLayerName,
        ecommerce,
        extraData
      });

      logInfo(`Tracking Sandbox GTM event {${analyticsEventName}} with params: ${JSON.stringify(ecommerce, null, 2)}`);
    }
    catch(error) {
      logError(`Failed to track Sandbox GTM event {${analyticsEventName}} with params: ${JSON.stringify(ecommerce)}`, error);
    }
  }

  private trackConsent(): void {
    if(!this.hasBeenInitialized) return;

    try {
      if(this.hasAcceptedCookies) {
        logInfo('Granting consent for Sandbox GTM.');
      }
      else {
        logInfo('Denying consent for Sandbox GTM.');
      }

      if(isNullOrUndefined(this.tagManager)) {
        return;
      }

      GtmHelper.grantUpdatedConsentToGtmTracker(this.tagManager?.dataLayerName, this.hasAcceptedCookies);
    }
    catch(error) {
      logError('Failed to track Sandbox GTM consent.', error);
    }
  }
}
